import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import tw from 'tailwind-styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import useCartActions from '../hooks/useCartAction';
import useCart from '../hooks/useCart';
import { Set_Cart_Modal_Open } from '../Redux/actions';
import Config from '../Config';
import { FaPlus, FaShoppingCart, FaStar, FaMinus, FaChevronRight, FaChevronDown, FaChevronLeft, FaArrowLeft } from 'react-icons/fa';
import Cart from '../components/Cart';
import EmailLoginModal from '../components/EmailLoginModal';
import LoginModal from '../Pages/User/Login';
import SignupModal from '../Pages/User/Register';
import RecommendedProducts from '../components/RecommendedProducts'

const ProductPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const user = useSelector((state) => state.UserReducer.user);
    const isCartModalOpen = useSelector((state) => state.CartReducer.isCartModalOpen);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('description');
    const [expandedSection, setExpandedSection] = useState(null);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isOTPLoginModalOpen, setIsOTPLoginModalOpen] = useState(false);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [stockError, setStockError] = useState('');

    // bottom padding to prevent overlap with bottom navigation
    useEffect(() => {
        const addBottomPadding = () => {
            // padding for mobile bottom navigation (64px) + mobile button container (80px)
            if (window.innerWidth <= 768 && !isCartModalOpen) {
                document.body.style.paddingBottom = '144px';
            } else {
                document.body.style.paddingBottom = '0px';
            }
        };

        addBottomPadding();
        window.addEventListener('resize', addBottomPadding);

        return () => {
            document.body.style.paddingBottom = '0px';
            window.removeEventListener('resize', addBottomPadding);
        };
    }, [isCartModalOpen]);

    // Cart actions
    const { addItem, updateItem, removeItem } = useCartActions();
    const isProcessing = addItem.isLoading || updateItem.isLoading || removeItem.isLoading;
    const { data: cart } = useCart();

    // Get product from navigation state or fetch from API
    const product = state?.productData;

    // Fallback API call if no product data is passed (for direct URL access)
    const { data: apiProduct, isLoading: productLoading } = useQuery(
        ['product', id],
        async () => {
            if (product) return { data: { data: product } };
            const response = await axios.get(`${Config.apiUrl}/products/${id}`);
            return response.data;
        },
        {
            enabled: !product && !!id,
        }
    );

    const currentProduct = product || apiProduct?.data?.data;
    const categoryId = currentProduct?.category_id;

    const { data: recommendedData, isLoading: recommendedLoading } = useQuery(
        ['recommendedProducts', categoryId, currentProduct?.id], // currentProduct.id key to refetch if product changes
        async () => {
            if (!categoryId) return []; // Don't fetch if no category ID
            // console.log(`Fetching recommendations for category: ${categoryId}, excluding product: ${currentProduct?.id}`);
            const response = await axios.get(`${Config.apiUrl}/products?category_id=${categoryId}&limit=8`);
            // Filter out the current product from recommendations
            const recommendations = (response.data?.data?.products || []).filter(p => p.id !== currentProduct?.id);
            // console.log("Recommended products fetched:", recommendations);
            return recommendations;
        },
        {
            enabled: !!categoryId && !!currentProduct?.id, // Only run query if categoryId and currentProduct.id are available
            staleTime: 1000 * 60 * 5, // Cache recommendations for 5 minutes
        }
    );
    const recommendedProducts = recommendedData || [];

    // Get cart item details for this product
    const cartItems = cart?.items || [];
    const cartItem = cartItems.find(item => item.product_id === currentProduct?.id);
    const inCartQty = cartItem?.quantity || 0;
    const cartItemId = cartItem?.id;

    useEffect(() => {
        if (!currentProduct && !productLoading) {
            navigate('/');
        }
    }, [currentProduct, productLoading, navigate]);

    if (productLoading || !currentProduct) {
        return (
            <LoadingContainer>
                <LoadingSpinner />
            </LoadingContainer>
        );
    }

    const handleOpenCartModal = () => {      // open cart modal if logged in, else open login modal
        if (user) {
            dispatch(Set_Cart_Modal_Open(true));
        } else {
            setIsLoginModalOpen(true);
        }
    };

    const handleCloseCartModal = () => {
        dispatch(Set_Cart_Modal_Open(false));
    };

    const handleOpenLoginModal = () => {
        setIsLoginModalOpen(true);
    };

    const handleCloseLoginModal = () => {
        setIsLoginModalOpen(false);
    };

    const handleCloseOTPLoginModal = () => {
        setIsOTPLoginModalOpen(false);
    };

    const handleCloseSignupModal = () => {
        setIsSignupModalOpen(false);
    };

    const images = currentProduct.images?.length > 0
        ? currentProduct.images.map(img => img.image_url)
        : ["https://cdn-academyblog.pressidium.com/wp-content/uploads/2020/08/IND-Nike-air-jordan-1-mid-shoe-819x1024.jpg"];

    const handleAddItem = () => {
        if (!user) {
            handleOpenLoginModal();
            return;
        }
        if (currentProduct.stock_quantity <= 0) {
            setStockError('Out of stock');
            return;
        }
        setStockError(''); // clear error

        addItem.mutate({
            product_id: product.id,
            variant_id: product.variant_id,
            quantity: 1,
        }, {
            onSuccess: () => {
                // toast.success(`${product.name} added to cart!`);
                    toast.success(`Item added to cart!`);
                dispatch(Set_Cart_Modal_Open(true));
            },
            onError: (error) => {
                // toast.error(`Failed to add ${product.name} to cart`);
                toast.error(`Failed to add item to cart`);

            },
        });
    };

    const handleIncreaseQty = () => {
        if (!user) {
            handleOpenLoginModal();
            return;
        }

        if (inCartQty >= currentProduct.stock_quantity) {
            setStockError(`Only ${currentProduct.stock_quantity} ${currentProduct.stock_quantity === 1 ? 'piece' : 'pieces'} left.`);
            return;
        }
        setStockError('');
        updateItem.mutate({
            id: cartItemId,
            quantity: inCartQty + 1,
        });
    };


    const handleDecrease = () => {
        setStockError('');
        if (inCartQty > 1) {
            updateItem.mutate({
                id: cartItemId,
                quantity: inCartQty - 1,
            });
        } else if (inCartQty === 1) {
            removeItem.mutate(cartItemId);
        }
    };

    const handleBuyNow = () => {
        if (!user) {
            handleOpenLoginModal();
            return;
        }

        if (inCartQty === 0) {
            handleAddItem();
        } else {
            dispatch(Set_Cart_Modal_Open(true));
        }
    };


    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    // Touch handlers for image swiping on mobile
    const handleTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && selectedImageIndex < images.length - 1) {
            setSelectedImageIndex(selectedImageIndex + 1);
        }
        if (isRightSwipe && selectedImageIndex > 0) {
            setSelectedImageIndex(selectedImageIndex - 1);
        }
    };

    // function to render star ratings
    const Stars = () => {
        const rating = currentProduct.average_rating;
        return [...Array(5)].map((_, i) => (
            <FaStar
                key={i}
                className={i < Math.floor(rating) ? 'text-yellow-600' : 'text-gray-300'}
            />
        ));
    };

    return (
        <Container>
            <Header>
                <BackButton onClick={() => navigate(-1)}>
                    <FaArrowLeft className='w-6 h-6 ' />
                </BackButton>
                <Title>{currentProduct?.category?.name}</Title>
                <CartButton onClick={handleOpenCartModal}>
                    <FaShoppingCart className="w-6 h-6" />
                    {/* {inCartQty > 0 && <CartBadge>{inCartQty}</CartBadge>} */}
                </CartButton>
            </Header>

            {/* Breadcrumb */}
            <Breadcrumb>
                <BreadcrumbItem onClick={() => navigate('/')}>Home</BreadcrumbItem>
                <FaChevronRight className='w-1 h-1' />
                <BreadcrumbItem>{currentProduct.category?.name || 'Products'}</BreadcrumbItem>
                {/* <FaChevronRight className='w-1.5 h-1.5' /> */}
                {/* <BreadcrumbItem >{currentProduct.name}</BreadcrumbItem> */}
            </Breadcrumb>

            <ContentWrapper>
                {/* Desktop Layout */}
                <DesktopLayout>
                    <ImageSection>
                        <MainImageContainer>
                            <MainImage src={images[selectedImageIndex]} alt={currentProduct.name} />

                            <StockTag className={currentProduct.stock_quantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {currentProduct.stock_quantity > 0 ? `In Stock` : 'Out of Stock'}
                            </StockTag>

                        </MainImageContainer>

                        {images.length > 1 && (
                            <ThumbnailContainer>
                                {images.map((image, index) => (
                                    <Thumbnail
                                        key={index}
                                        src={image}
                                        alt={`${currentProduct.name} ${index + 1}`}
                                        className={selectedImageIndex === index ? 'ring-2 ring-kuality-blue' : ''}
                                        onClick={() => setSelectedImageIndex(index)}
                                    />
                                ))}
                            </ThumbnailContainer>
                        )}
                    </ImageSection>

                    <DetailsSection>
                        <ProductInfo>
                            <ProductTitle>{currentProduct.name}</ProductTitle>
                            <Brand>{currentProduct.brand}</Brand>

                            <Rating>
                                <StarContainer>
                                    <Stars />
                                </StarContainer>
                                <RatingText>
                                    {currentProduct.average_rating ? (
                                        <>
                                            {parseFloat(currentProduct.average_rating).toFixed(1)} ({currentProduct.total_reviews} Reviews)
                                        </>
                                    ) : (
                                        `${currentProduct.total_reviews} Reviews`
                                    )}
                                </RatingText>
                            </Rating>

                            <PriceContainer>
                                {currentProduct.discount_price ? (
                                    <>
                                        <CurrentPrice>₹{parseFloat(currentProduct.discount_price).toFixed(2)}</CurrentPrice>
                                        <OriginalPrice>₹{parseFloat(currentProduct.price).toFixed(2)}</OriginalPrice>
                                    </>
                                ) : (
                                    <CurrentPrice>₹{parseFloat(currentProduct.price).toFixed(2)}</CurrentPrice>
                                )}
                            </PriceContainer>

                            <Description>{currentProduct.description}</Description>
                            {/* <StockItems>Items available in stock: {currentProduct.stock_quantity}</StockItems> */}
                            {/* <div>
                                <h3 className="font-semibold">Size
                                    : {currentProduct.size || 'N/A'}
                                </h3>
                            </div> */}

                            {stockError && (
                                <StockErrorText className="text-left">{stockError}</StockErrorText>
                            )}

                            <ButtonContainer>
                                {inCartQty > 0 ? (
                                    <QuantitySection>
                                        <QuantityButton className="border-r border-gray-300" onClick={handleDecrease} disabled={isProcessing}>
                                            <FaMinus />
                                        </QuantityButton>
                                        <QuantityValue>{isProcessing ? "..." : inCartQty}</QuantityValue>
                                        <QuantityButton className="border-l border-gray-300" onClick={handleIncreaseQty} disabled={isProcessing || currentProduct.stock_quantity <= 0}>
                                            <FaPlus />
                                        </QuantityButton>
                                    </QuantitySection>
                                ) : (
                                    <AddToCartBtn onClick={handleAddItem} disabled={isProcessing || currentProduct.stock_quantity <= 0}>
                                        {/* {isProcessing ? "Adding..." : "Add"} */} Add to Cart
                                    </AddToCartBtn>
                                )}
                                <BuyNowBtn onClick={handleBuyNow}>
                                    Buy Now
                                </BuyNowBtn>
                            </ButtonContainer>

                            <Seller>Sold By: {currentProduct.seller?.company_name}</Seller>
                        </ProductInfo>
                    </DetailsSection>
                </DesktopLayout>

                {/* Mobile Layout */}
                <MobileLayout>
                    {/* Mobile Image Carousel */}
                    <MobileImageContainer
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <MobileImage src={images[selectedImageIndex]} alt={currentProduct.name} />
                        <StockTag className={currentProduct.stock_quantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {currentProduct.stock_quantity > 0 ? `In Stock` : 'Out of Stock'}
                        </StockTag>
                        {images.length > 1 && (
                            <ImageIndicators>
                                {images.map((_, index) => (
                                    <Indicator
                                        key={index}
                                        className={selectedImageIndex === index ? 'bg-gray-800' : 'bg-gray-400'}
                                        onClick={() => setSelectedImageIndex(index)}
                                    />
                                ))}
                            </ImageIndicators>
                        )}
                    </MobileImageContainer>

                    {/* Mobile Product Info */}
                    <MobileProductInfo>
                        <MobileProductTitle>{currentProduct.name}</MobileProductTitle>
                        <MobileRating>
                            <StarContainer>
                                <Stars />
                            </StarContainer>

                            <RatingText>
                                {currentProduct.average_rating ? (
                                    <>
                                        {parseFloat(currentProduct.average_rating).toFixed(1)} ({currentProduct.total_reviews} Reviews)
                                    </>
                                ) : (
                                    `${currentProduct.total_reviews || 0} Reviews`
                                )}
                            </RatingText>
                        </MobileRating>

                        <MobilePriceContainer>
                            {currentProduct.discount_price ? (
                                <>
                                    <MobileCurrentPrice>₹{parseFloat(currentProduct.discount_price).toFixed(2)}</MobileCurrentPrice>
                                    <MobileOriginalPrice>₹{parseFloat(currentProduct.price).toFixed(2)}</MobileOriginalPrice>
                                </>
                            ) : (
                                <MobileCurrentPrice>₹{parseFloat(currentProduct.price).toFixed(2)}</MobileCurrentPrice>
                            )}
                        </MobilePriceContainer>
                        {/* <StockItems>Items available in stock: {currentProduct.stock_quantity}</StockItems> */}

                        {/* Mobile Thumbnails */}
                        {images.length > 1 && (
                            <MobileThumbnailContainer>
                                {images.map((image, index) => (
                                    <MobileThumbnail
                                        key={index}
                                        src={image}
                                        alt={`${currentProduct.name} ${index + 1}`}
                                        className={selectedImageIndex === index ? 'ring-2 ring-kuality-blue' : ''}
                                        onClick={() => setSelectedImageIndex(index)}
                                    />
                                ))}
                            </MobileThumbnailContainer>
                        )}

                        {/* Mobile Collapsible Sections */}
                        <MobileSection>
                            <MobileSectionHeader onClick={() => toggleSection('description')}>
                                <span>Description</span>
                                <FaChevronDown className={expandedSection === 'description' ? 'rotate-180' : ''} />
                            </MobileSectionHeader>
                            {expandedSection === 'description' && (
                                <MobileSectionContent>
                                    {currentProduct.description}
                                </MobileSectionContent>
                            )}
                        </MobileSection>

                        <MobileSection>
                            <MobileSectionHeader onClick={() => toggleSection('specifications')}>
                                <span>Specifications</span>
                                <FaChevronDown className={expandedSection === 'specifications' ? 'rotate-180' : ''} />
                            </MobileSectionHeader>
                            {expandedSection === 'specifications' && (
                                <MobileSectionContent>
                                    <SpecItem>
                                        <SpecLabel>Brand:</SpecLabel>
                                        <SpecValue>{currentProduct.brand}</SpecValue>
                                    </SpecItem>
                                    <SpecItem>
                                        <SpecLabel>Category:</SpecLabel>
                                        <SpecValue>{currentProduct.category?.name}</SpecValue>
                                    </SpecItem>
                                    {/* <SpecItem>
                                        <SpecLabel>Stock Quantity:</SpecLabel>
                                        <SpecValue>{currentProduct.stock_quantity}</SpecValue>
                                    </SpecItem> */}
                                </MobileSectionContent>
                            )}
                        </MobileSection>

                        <MobileSection>
                            <MobileSectionHeader onClick={() => toggleSection('reviews')}>
                                <span>Reviews</span>
                                <FaChevronDown className={expandedSection === 'reviews' ? 'rotate-180' : ''} />
                            </MobileSectionHeader>
                            {expandedSection === 'reviews' && (
                                <MobileSectionContent>
                                    {currentProduct.total_reviews > 0 ? (
                                        <div className="text-center py-4 text-gray-500">
                                            {currentProduct.total_reviews} Reviews
                                        </div>
                                    ) : (
                                        <div className="text-center py-4 text-gray-500">
                                            No reviews yet.
                                        </div>
                                    )}
                                </MobileSectionContent>
                            )}
                        </MobileSection>
                    </MobileProductInfo>
                </MobileLayout>

                {/* Desktop Tabs */}
                <DesktopTabsContainer>
                    <TabButtons>
                        <TabButton
                            className={activeTab === 'description' ? 'active' : ''}
                            onClick={() => setActiveTab('description')}
                        >
                            Description
                        </TabButton>
                        <TabButton
                            className={activeTab === 'specifications' ? 'active' : ''}
                            onClick={() => setActiveTab('specifications')}
                        >
                            Specifications
                        </TabButton>
                        <TabButton
                            className={activeTab === 'reviews' ? 'active' : ''}
                            onClick={() => setActiveTab('reviews')}
                        >
                            Reviews
                        </TabButton>
                    </TabButtons>

                    <TabContent>
                        {activeTab === 'description' && (
                            <TabPanel>
                                <p>{currentProduct.description}</p>
                            </TabPanel>
                        )}
                        {activeTab === 'specifications' && (
                            <TabPanel>
                                <SpecItem>
                                    <SpecLabel>Brand:</SpecLabel>
                                    <SpecValue>{currentProduct.brand}</SpecValue>
                                </SpecItem>
                                <SpecItem>
                                    <SpecLabel>Category:</SpecLabel>
                                    <SpecValue>{currentProduct.category?.name}</SpecValue>
                                </SpecItem>
                                {/* <SpecItem>
                                    <SpecLabel>Stock Quantity:</SpecLabel>
                                    <SpecValue>{currentProduct.stock_quantity}</SpecValue>
                                </SpecItem> */}
                            </TabPanel>
                        )}
                        {activeTab === 'reviews' && (
                            <TabPanel>
                                {currentProduct.total_reviews > 0 ? (
                                    <div className="text-center py-4 text-gray-500">
                                        {currentProduct.total_reviews} Reviews
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-gray-500">
                                        No reviews yet.
                                    </div>
                                )}
                            </TabPanel>
                        )}
                    </TabContent>
                </DesktopTabsContainer>
            </ContentWrapper>
            
                {!recommendedLoading && recommendedProducts.length > 0 && (
                    <RecommendedProducts
                        products={recommendedProducts}
                        onOpenLoginModal={handleOpenLoginModal} // Pass the function
                    />
                )}

            {/* Mobile Buttons */}
            <MobileButtonContainer className='flex flex-col py-1'>
                <div >
                    {stockError && (
                        <StockErrorText className="text-center">{stockError}</StockErrorText>
                    )}
                </div>
                <div className='flex flex-row gap-2'>
                    {inCartQty > 0 ? (
                        <MobileQuantitySection>
                            <MobileQuantityButton className="border-r border-gray-300" onClick={handleDecrease} disabled={isProcessing}>
                                <FaMinus />
                            </MobileQuantityButton>
                            <MobileQuantityValue>{isProcessing ? "..." : inCartQty}</MobileQuantityValue>
                            <MobileQuantityButton className="border-l border-gray-300" onClick={handleIncreaseQty} disabled={isProcessing || currentProduct.stock_quantity <= 0}>
                                <FaPlus />
                            </MobileQuantityButton>
                        </MobileQuantitySection>
                    ) : (
                        <MobileAddToCartBtn onClick={handleAddItem} disabled={isProcessing || currentProduct.stock_quantity <= 0}>
                            {/* {isProcessing ? 'Adding...' : 'Add to Cart'} */} Add to Cart
                        </MobileAddToCartBtn>
                    )}
                    <MobileBuyNowBtn onClick={handleBuyNow}>
                        Buy Now
                    </MobileBuyNowBtn>
                </div>
            </MobileButtonContainer>

            {/* Cart Modal */}
            <Cart isOpen={isCartModalOpen} onClose={handleCloseCartModal} />

            {/* Modals */}
            <EmailLoginModal
                isOpen={isLoginModalOpen}
                onClose={handleCloseLoginModal}
                onSwitchToSignup={() => {
                    setIsLoginModalOpen(false);
                    setIsSignupModalOpen(true);
                }}
                onSwitchToEmailLogin={() => {
                    setIsLoginModalOpen(false);
                    setIsOTPLoginModalOpen(true);
                }}
                onLoginSuccess={() => {
                    handleCloseLoginModal();
                }}
            />
            <LoginModal
                isOpen={isOTPLoginModalOpen}
                onClose={handleCloseOTPLoginModal}
                onSwitchToSignup={() => {
                    setIsOTPLoginModalOpen(false);
                    setIsSignupModalOpen(true);
                }}
                onLoginSuccess={() => {
                    handleCloseOTPLoginModal();
                }}
            />
            <SignupModal
                isOpen={isSignupModalOpen}
                onClose={handleCloseSignupModal}
                onSwitchToLogin={() => {
                    setIsSignupModalOpen(false);
                    setIsLoginModalOpen(true);
                }}
                onSignupSuccess={() => {
                    handleCloseSignupModal();
                }}
            />
        </Container>
    );
};

// Styled Components
const Container = tw.div`min-h-screen bg-white`;
const LoadingContainer = tw.div`flex justify-center items-center py-20`;
const LoadingSpinner = tw.div`animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600`;

// Header
const Header = tw.div`flex items-center justify-between p-3 md:p-2 text-white sticky top-0 z-50 border-b border-gray-200 bg-kuality-blue`;
const BackButton = tw.button`p-2 hover:text-gray-200 rounded`;
const Title = tw.h1`text-xl md:text-2xl `;
const CartButton = tw.button`relative p-2 hover:text-gray-200`;
const CartBadge = tw.span`absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center`;

// Breadcrumb
const Breadcrumb = tw.div`flex items-center gap-1 px-4 py-1 text-xs text-gray-600`;
const BreadcrumbItem = tw.span`hover:text-kuality-dark cursor-pointer`;

const ContentWrapper = tw.div` mx-auto px-4 pb-20 md:pb-8`;

// Desktop Layout
const DesktopLayout = tw.div`hidden md:grid md:grid-cols-12 gap-10 mb-8 items-start`;

// Image Section
const ImageSection = tw.div`md:col-span-5 space-y-4`;
const MainImageContainer = tw.div`relative overflow-hidden aspect-square`;
const MainImage = tw.img`w-full h-full object-contain`;
const ThumbnailContainer = tw.div`flex gap-2 overflow-x-auto`;
const Thumbnail = tw.img`w-20 h-20 object-cover cursor-pointer hover:ring-2 hover:ring-gray-300 flex-shrink-0`;

// Details Section
const DetailsSection = tw.div`md:col-span-7 space-y-6`;
const ProductInfo = tw.div`space-y-4`;
const ProductTitle = tw.h1`text-3xl font-bold text-gray-800`;
const Brand = tw.span`text-lg text-gray-700`;

// Rating
const Rating = tw.div`flex items-center gap-2`;
const StarContainer = tw.div`flex`;
const RatingText = tw.span`text-gray-500`;

// Price
const PriceContainer = tw.div`flex items-center gap-3`;
const CurrentPrice = tw.span`text-3xl font-bold text-black`;
const OriginalPrice = tw.span`text-xl text-gray-400 line-through`;

const Description = tw.p`text-gray-600 leading-relaxed`;

const StockTag = tw.span`absolute top-2 right-2 text-sm font-semibold px-0.5 py-0.5 border`;
const StockItems = tw.span`text-sm text-gray-600 font-semibold`;
// Quantity
const QuantitySection = tw.div`flex items-center border border-gray-300 w-fit`;
const QuantityButton = tw.button`px-3 py-2 hover:bg-gray-100`;
const QuantityValue = tw.span`px-1 py-2 min-w-[3rem] text-center`;

const StockErrorText = tw.p`
  text-red-600 text-xs font-medium w-full text-center
`;

// Buttons
const ButtonContainer = tw.div`flex gap-4`;
const AddToCartBtn = tw.button`px-5 py-2 bg-kuality-blue text-white hover:bg-kuality-dark disabled:opacity-50 font-semibold`;
const BuyNowBtn = tw.button`px-5 py-2 bg-gray-600 text-white hover:bg-gray-700 font-semibold`;

const Seller = tw.p`text-md text-gray-700`;

// Mobile Layout
const MobileLayout = tw.div`md:hidden`;
const MobileImageContainer = tw.div`relative aspect-square mb-4`;
const MobileImage = tw.img`w-full h-full object-contain`;
const ImageIndicators = tw.div`absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2`;
const Indicator = tw.button`w-2 h-2 rounded-full`;

const MobileProductInfo = tw.div`px-4 space-y-4`;
const MobileProductTitle = tw.h1`text-xl font-bold text-gray-800`;
const MobileRating = tw.div`flex items-center gap-2`;
const MobilePriceContainer = tw.div`flex items-center gap-3 mb-4`;
const MobileCurrentPrice = tw.span`text-2xl font-bold`;
const MobileOriginalPrice = tw.span`text-lg text-gray-400 line-through`;

const MobileThumbnailContainer = tw.div`flex gap-2 overflow-x-auto pb-2`;
const MobileThumbnail = tw.img`w-16 h-16 object-cover border cursor-pointer flex-shrink-0`;

// Mobile Collapsible Sections
const MobileSection = tw.div`border-b border-gray-200`;
const MobileSectionHeader = tw.button`w-full flex justify-between items-center py-4 text-left font-medium`;
const ChevronIcon = tw.span`transform transition-transform duration-200`;
const MobileSectionContent = tw.div`pb-4 text-gray-600`;

// Desktop Tabs
const DesktopTabsContainer = tw.div`hidden md:block mt-12`;
const TabButtons = tw.div`flex border-b border-gray-200`;
const TabButton = tw.button`px-6 py-3 font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300
    ${(props) => props.className?.includes('active') ? 'text-kuality-blue border-kuality-blue' : ''}`;
const TabContent = tw.div`mt-6`;
const TabPanel = tw.div`space-y-4 my-2`;

// Specifications
const SpecItem = tw.div`flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0`;
const SpecLabel = tw.span`font-medium`;
const SpecValue = tw.span`text-gray-600`;

// Mobile Buttons
const MobileButtonContainer = tw.div`md:hidden fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 z-40`;
const MobileAddToCartBtn = tw.button`flex-1 px-3 py-3 bg-kuality-blue text-white font-semibold disabled:opacity-50`;
const MobileBuyNowBtn = tw.button`flex-1 px-3 py-3 bg-gray-600 text-white font-semibold`;

// Mobile Quantity Section
const MobileQuantitySection = tw.div`flex-1 flex items-center border border-gray-300 bg-white`;
const MobileQuantityButton = tw.button`px-4 py-2 hover:bg-gray-100 disabled:opacity-50`;
const MobileQuantityValue = tw.span`px-1 py-2 min-w-[3rem] text-center font-semibold flex-1`;

export default ProductPage;
